import moment from 'moment';
import { Dropdown, RadioButton, RadioButtonGroup } from '@carbon/react';
import {
  TextField,
  TextAreaField,
  TanstackFieldWrapper,
} from 'components/form-fields-2';
import MonthPicker from 'components/month-picker/month-picker';
import { ReorderableList } from 'components/reorderable-list/reorderable-list';
import { useTypeCurveScenarioOptions } from 'components/type-curve-scenario-picker/use-type-curve-scenario-options';
import { useInventorySummary } from '../use-inventory-summary';
import { InventoryChips } from './capture/inventory-chips';
import { useScenarioForm } from './contexts';
import {
  PACE_MODEL_OPTIONS,
  SLOT_PRIORITIZATION_OPTIONS,
  buildTypeCurveDropdownItems,
  validateRequired,
  type TypeCurveDropdownItem,
} from './utils';
import styles from './global-configs.module.scss';

const CURRENT_MONTH = moment().format('MMMM YYYY');

/**
 * Global configuration form section. Rendered when "Global" is selected
 * in the sidebar. Reads/writes form state via useScenarioForm() context.
 * @return {JSX.Element} The global configs form
 */
export function GlobalConfigs(): JSX.Element {
  const { form } = useScenarioForm();

  return (
    <div className={styles.globalConfigs}>
      <div className={styles.scenarioRow}>
        <div className={styles.scenarioCol}>
          <h4 className={styles.sectionTitle}>Configuration Details</h4>

          <form.Field
            name="global.configurationName"
            validators={{
              onMount: ({ value }: { value: string }) =>
                validateRequired(value),
              onChange: ({ value }: { value: string }) =>
                validateRequired(value),
            }}
          >
            {(field: any) => (
              <TanstackFieldWrapper<string> field={field}>
                {(props) => (
                  <TextField
                    id="configuration-name"
                    labelText="Configuration Name (required)"
                    placeholder="Enter configuration name"
                    {...props}
                  />
                )}
              </TanstackFieldWrapper>
            )}
          </form.Field>

          <form.Field name="global.notes">
            {(field: any) => (
              <TanstackFieldWrapper<string> field={field}>
                {(props) => (
                  <TextAreaField
                    id="scenario-notes"
                    labelText="Notes"
                    placeholder="Add any notes or context for this configuration..."
                    rows={4}
                    {...props}
                  />
                )}
              </TanstackFieldWrapper>
            )}
          </form.Field>
        </div>

        <div className={styles.priorityCol}>
          <h4 className={styles.sectionTitle}>Geometry Priority</h4>
          <p className={styles.sectionDescription}>
            When a Slot intersects multiple Area types, the higher-ranked type
            takes precedence. Overlaps between different-ranked types are
            allowed.
          </p>
          <form.Field name="global.geometryPriority">
            {(field: any) => (
              <ReorderableList
                items={field.state.value}
                onReorder={(items) => field.handleChange(items)}
              />
            )}
          </form.Field>
        </div>
      </div>

      <h4 className={styles.sectionTitle}>Configuration</h4>
      <p className={styles.sectionDescription}>
        These settings are scenario-wide and apply to every area geometry group.
      </p>

      <div className={styles.fieldRow}>
        <form.Field name="global.referenceDateMode">
          {(field: any) => (
            <RadioButtonGroup
              legendText="Development Pace Reference Date"
              name="reference-date-mode"
              valueSelected={field.state.value}
              onChange={(value: string | number | undefined) => {
                field.handleChange(value);
                if (value === 'current_month') {
                  form.setFieldValue(
                    'global.referenceDate',
                    moment().startOf('month').format('YYYY-MM-DD')
                  );
                }
              }}
            >
              <RadioButton
                id="ref-date-current"
                labelText={`Current Month (${CURRENT_MONTH})`}
                value="current_month"
              />
              <RadioButton
                id="ref-date-fixed"
                labelText="Fixed"
                value="fixed"
              />
            </RadioButtonGroup>
          )}
        </form.Field>
      </div>

      <form.Subscribe
        selector={(state) => state.values.global.referenceDateMode}
      >
        {(mode) => {
          return mode === 'fixed' ? (
            <div className={styles.paramsGrid}>
              <form.Field
                name="global.referenceDate"
                validators={{
                  onChange: ({ value }: { value: string }) =>
                    validateRequired(value),
                }}
              >
                {(field: any) => {
                  const errors = field.state.meta.errors || [];
                  const errorText =
                    typeof errors[0] === 'string' ? errors[0] : undefined;
                  return (
                    <MonthPicker
                      label="Reference Date"
                      light
                      isInvalid={!!errorText}
                      invalidText={errorText}
                      selectedMonth={
                        field.state.value ? moment(field.state.value) : null
                      }
                      setSelectedMonth={(month, year) => {
                        field.handleChange(
                          moment({ year, month })
                            .startOf('month')
                            .format('YYYY-MM-DD')
                        );
                      }}
                    />
                  );
                }}
              </form.Field>
            </div>
          ) : null;
        }}
      </form.Subscribe>

      <div className={styles.formGrid}>
        <form.Field name="global.paceModel">
          {(field: any) => (
            <Dropdown
              id="dev-pace-model"
              titleText="Development Pace Model"
              label=""
              size="md"
              light
              items={PACE_MODEL_OPTIONS.map((o) => o.value)}
              itemToString={(item: string) =>
                PACE_MODEL_OPTIONS.find((o) => o.value === item)?.label || item
              }
              selectedItem={field.state.value}
              onChange={({ selectedItem }: { selectedItem: string }) => {
                field.handleChange(selectedItem);
                // The per-area pace validator is pace-model-dependent, so a
                // value that was valid under the old model may now be out of
                // range — re-validate the form to re-gate save immediately.
                form.validate('change');
              }}
            />
          )}
        </form.Field>

        <TypeCurveScenarioField />

        <Dropdown
          id="slot-prioritization"
          titleText="Slot Prioritization Method"
          label=""
          size="md"
          light
          items={SLOT_PRIORITIZATION_OPTIONS.map((o) => o.value)}
          itemToString={() => SLOT_PRIORITIZATION_OPTIONS[0].label}
          selectedItem={SLOT_PRIORITIZATION_OPTIONS[0].value}
          disabled
          helperText={SLOT_PRIORITIZATION_OPTIONS[0].helperText}
        />
      </div>

      <div className={styles.fallbackRow}>
        <p className={styles.fallbackDescription}>
          Slots outside your areas are not scheduled.
        </p>
      </div>
    </div>
  );
}

/**
 * The required Type Curve Scenario picker. Its options are the org's promoted
 * scenarios (name-ordered from the endpoint). A saved selection that has since
 * lost its promoted content still shows — appended as a flagged "(no longer
 * promoted)" option — so the choice is never silently swapped or blanked (5.5).
 * @return {JSX.Element} The type curve scenario dropdown field
 */
function TypeCurveScenarioField(): JSX.Element {
  const { form } = useScenarioForm();
  const { options, loading } = useTypeCurveScenarioOptions();

  return (
    <form.Subscribe
      selector={(state) => ({
        id: state.values.global.typeCurveScenarioId,
        name: state.values.global.typeCurveScenarioName,
      })}
    >
      {({ id, name }) => {
        const items = buildTypeCurveDropdownItems(options, id, name);
        const selectedItem = items.find((item) => item.id === id) ?? undefined;

        return (
          <>
            <form.Field name="global.typeCurveScenarioId">
              {(field: any) => (
                <Dropdown<TypeCurveDropdownItem>
                  id="type-curve-scenario"
                  titleText="Type Curve Scenario (required)"
                  label="Choose an option"
                  size="md"
                  light
                  disabled={loading}
                  items={items}
                  itemToString={(item) => item?.label ?? ''}
                  selectedItem={selectedItem}
                  onChange={({
                    selectedItem: chosen,
                  }: {
                    selectedItem: TypeCurveDropdownItem | null;
                  }) => {
                    field.handleChange(chosen?.id ?? null);
                    // Store the CLEAN promoted name (not the item's label, which
                    // for the revoked selection carries the "(no longer promoted)"
                    // suffix and would compound on re-select). A flagged/synthetic
                    // choice has no promoted match, so its name resets to null.
                    const promoted = chosen
                      ? options.find((option) => option.id === chosen.id)
                      : undefined;
                    form.setFieldValue(
                      'global.typeCurveScenarioName',
                      promoted?.name ?? null
                    );
                  }}
                />
              )}
            </form.Field>
            {/* Org-wide scope chips: prompt until a scenario is chosen (AC 1). */}
            <InventoryChipsField typeCurveScenarioId={id} />
          </>
        );
      }}
    </form.Subscribe>
  );
}

interface InventoryChipsFieldProps {
  typeCurveScenarioId: number | null;
}

/**
 * Wires the org-wide inventory-summary read to the scope chips. A NULL selection
 * disables the read (prompt state, no compute — AC 1).
 * @param {InventoryChipsFieldProps} props - The selected scenario id.
 * @return {JSX.Element} The scope chips or prompt.
 */
function InventoryChipsField({
  typeCurveScenarioId,
}: InventoryChipsFieldProps): JSX.Element {
  const { summary, loading } = useInventorySummary(typeCurveScenarioId);
  return (
    <InventoryChips
      summary={summary}
      loading={loading}
      hasSelection={typeCurveScenarioId !== null}
    />
  );
}
